import { MouseEvent } from 'react';
import cn from 'clsx';
import Link from '@v2/shared/components/Link';
import Price from '@v2/shared/components/Price';
import { ExpandedProduct, GroupedProduct, Sku, Variant } from '@v2/shared/lib/types';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { findSkuByVariants, VariantSelector } from './variants';

type Props = {
  selectors: VariantSelector[];
  activeSkus: Sku[];
  selectedSku: Sku;
  skuProducts: Map<number, GroupedProduct | ExpandedProduct>;
  /** Carried onto every option's url, so switching format does not drop the coupon. */
  offerCode: string | null;
  selectVariant: (variant: Variant, event?: { preventDefault: () => void }) => void;
};

/**
 * The format and term chooser — "Paperback / Hardcover", "1 Year / 2 Years".
 *
 * Each option is an anchor to the page that option leads to, even when picking it will
 * be handled in place. That is deliberate and is what makes the alternate formats
 * crawlable and openable in a new tab; `selectVariant` cancels the navigation only when
 * the destination is this same product.
 *
 * A price is drawn under the option only on the Term selector. On a Format selector the
 * price belongs to the whole product and is already the headline above.
 */
export default function FormatSelector({
  selectors,
  activeSkus,
  selectedSku,
  skuProducts,
  offerCode,
  selectVariant,
}: Props) {
  const { buildUrl } = useUrlGenerator();
  const offerQuery = offerCode ? `?offer=${encodeURIComponent(offerCode)}` : '';

  return (
    <>
      {selectors.map((selector) => (
        <div key={selector.id} className="mb-5 flex justify-center flex-wrap">
          {selector.options.map((option) => {
            const sku = findSkuByVariants(activeSkus, selectedSku.variants, option.variant);

            if (!sku) {
              return null;
            }

            const product = skuProducts.get(sku.id);

            return (
              <div key={option.value} className="select-none w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 p-2">
                <Link
                  href={buildUrl(`${product?.href ?? ''}/${sku.code}${offerQuery}`)}
                  onClick={(event: MouseEvent<HTMLAnchorElement>) => selectVariant(option.variant, event)}
                  className={cn(
                    'group block cursor-pointer p-1 py-1.5 border-2 text-slate-600',
                    'active:bg-red-50 active:border-red-600 active:text-red-900',
                    option.selected
                      ? 'border-red-600 text-red-900 bg-red-50/50'
                      : 'border-slate-300 hover:bg-slate-50 hover:border-slate-400'
                  )}
                >
                  <div className="text-center items-center">
                    <div className="font-semibold text-sm">{option.text}</div>
                    {selector.name === 'Term' && <Price value={sku.sale_price || sku.price} />}
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}
